import React, { useState, useEffect, useMemo } from 'react';
import { fetchUserById, updateUser, verifyAdmin } from '../../services/adminService';
import { doc, onSnapshot, getDoc } from 'firebase/firestore';
import { db, auth } from '../../firebase/config';
import { useAdminAuth } from '../../hooks/useAdminAuth';
import { checkVIPUpgradeEligibility, processVIPUpgrade, VIP_UPGRADE_REQUIREMENTS } from '../../services/vipUpgradeService';
import { getUserOptimizationData } from '../../services/optimizationService';
import { resetUserProgress } from '../../services/dailyResetService';
import { VIP_CONFIG } from '../../services/optimizationService';

function UserEditForm({ userId, onBack, onUpdate }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    username: '',
    vipLevel: 'VIP1',
    creditScore: 100,
    wallet: 0,
    frozen: 0,
    commission: 0,
    total: 0,
    isActive: true
  });
  const [reason, setReason] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [vipEligibility, setVipEligibility] = useState(null);
  const [upgrading, setUpgrading] = useState(false);
  const [selectedVipLevel, setSelectedVipLevel] = useState(null); // Track selected VIP level from dropdown
  const [vipUpgradeInfo, setVipUpgradeInfo] = useState(null); // Info for selected VIP level upgrade
  const [optimizationStatus, setOptimizationStatus] = useState(null); // User's optimization progress
  const [resettingProgress, setResettingProgress] = useState(false); // Loading state for reset
  const { isAdmin } = useAdminAuth();

  useEffect(() => {
    loadUser();
    
    // Set up real-time listener for immediate updates when admin makes changes
    const userRef = doc(db, 'users', userId);
    const unsubscribe = onSnapshot(
      userRef,
      (docSnapshot) => {
        if (docSnapshot.exists()) {
          const userData = { id: docSnapshot.id, ...docSnapshot.data() };
          setUser(userData);
          // Calculate total: Total = Wallet + Commission (excluding frozen)
          const calculatedTotal = (userData.balances?.wallet || 0) + (userData.balances?.commission || 0);
          const currentVip = userData.vipLevel || 'VIP1';
      setFormData({
        email: userData.email || '',
        username: userData.username || '',
        vipLevel: currentVip,
        creditScore: userData.creditScore ?? 100,
        wallet: userData.balances?.wallet || 0,
        frozen: userData.balances?.frozen || 0,
        commission: userData.balances?.commission || 0,
        total: calculatedTotal,
        isActive: userData.isActive !== false
      });
      // Reset selected VIP level when user data updates
      setSelectedVipLevel(null);
      setVipUpgradeInfo(null);
      
      // Load optimization status when user data is loaded
      loadOptimizationStatus();
        }
      },
      (error) => {
        console.error('Error in real-time listener:', error);
      }
    );

    return () => unsubscribe();
  }, [userId]);

  // Set up real-time listener for optimization status to refresh immediately
  useEffect(() => {
    if (!userId) return;
    
    const statusRef = doc(db, 'optimizationStatus', userId);
    const unsubscribe = onSnapshot(
      statusRef,
      async (docSnapshot) => {
        if (docSnapshot.exists()) {
          const data = docSnapshot.data();
          const vipLevel = formData.vipLevel || user?.vipLevel || 'VIP1';
          
          // Get full optimization data for accurate status
          try {
            const optimizationData = await getUserOptimizationData(userId, vipLevel);
            if (optimizationData) {
              setOptimizationStatus({
                completedSets: data.completedSets || 0,
                currentTask: optimizationData.currentTask || 0,
                tasksCompleted: optimizationData.tasksCompleted || 0,
                currentOptimization: optimizationData.currentOptimization || 1,
                isLocked: data.isLocked || false,
                vipLevel: vipLevel,
                optimizations: optimizationData.optimizations || {}
              });
            } else {
              setOptimizationStatus({
                completedSets: data.completedSets || 0,
                currentTask: data.currentTask || 0,
                tasksCompleted: data.tasksCompleted || 0,
                currentOptimization: data.currentOptimization || 1,
                isLocked: data.isLocked || false,
                vipLevel: vipLevel,
                optimizations: data.optimizations || {}
              });
            }
          } catch (error) {
            // Fallback to status data only
            setOptimizationStatus({
              completedSets: data.completedSets || 0,
              currentTask: data.currentTask || 0,
              tasksCompleted: data.tasksCompleted || 0,
              currentOptimization: data.currentOptimization || 1,
              isLocked: data.isLocked || false,
              vipLevel: vipLevel,
              optimizations: data.optimizations || {}
            });
          }
        } else {
          // No status document, set defaults
          const vipLevel = formData.vipLevel || user?.vipLevel || 'VIP1';
          setOptimizationStatus({
            completedSets: 0,
            currentTask: 0,
            tasksCompleted: 0,
            currentOptimization: 1,
            isLocked: false,
            vipLevel: vipLevel,
            optimizations: {}
          });
        }
      },
      (error) => {
        console.error('Error in optimization status listener:', error);
      }
    );

    return () => unsubscribe();
  }, [userId, formData.vipLevel, user?.vipLevel]);

  // Set up real-time listener for optimization status
  useEffect(() => {
    if (!userId) return;
    
    const statusRef = doc(db, 'optimizationStatus', userId);
    const unsubscribe = onSnapshot(
      statusRef,
      (docSnapshot) => {
        if (docSnapshot.exists()) {
          const data = docSnapshot.data();
          const vipLevel = formData.vipLevel || user?.vipLevel || 'VIP1';
          
          // Also get full optimization data
          getUserOptimizationData(userId, vipLevel).then(optimizationData => {
            if (optimizationData) {
              setOptimizationStatus({
                completedSets: data.completedSets || 0,
                currentTask: optimizationData.currentTask || 0,
                tasksCompleted: optimizationData.tasksCompleted || 0,
                currentOptimization: optimizationData.currentOptimization || 1,
                isLocked: data.isLocked || false,
                vipLevel: vipLevel,
                optimizations: optimizationData.optimizations || {}
              });
            } else {
              setOptimizationStatus({
                completedSets: data.completedSets || 0,
                currentTask: data.currentTask || 0,
                tasksCompleted: data.tasksCompleted || 0,
                currentOptimization: data.currentOptimization || 1,
                isLocked: data.isLocked || false,
                vipLevel: vipLevel,
                optimizations: data.optimizations || {}
              });
            }
          }).catch(() => {
            // Fallback to status data only
            setOptimizationStatus({
              completedSets: data.completedSets || 0,
              currentTask: data.currentTask || 0,
              tasksCompleted: data.tasksCompleted || 0,
              currentOptimization: data.currentOptimization || 1,
              isLocked: data.isLocked || false,
              vipLevel: vipLevel,
              optimizations: data.optimizations || {}
            });
          });
        } else {
          // No status document, set defaults
          const vipLevel = formData.vipLevel || user?.vipLevel || 'VIP1';
          setOptimizationStatus({
            completedSets: 0,
            currentTask: 0,
            tasksCompleted: 0,
            currentOptimization: 1,
            isLocked: false,
            vipLevel: vipLevel,
            optimizations: {}
          });
        }
      },
      (error) => {
        console.error('Error in optimization status listener:', error);
      }
    );

    return () => unsubscribe();
  }, [userId, formData.vipLevel, user?.vipLevel]);

  // Check VIP upgrade eligibility when user data or selected VIP level changes
  useEffect(() => {
    const checkUpgradeEligibility = async () => {
      if (!user || !selectedVipLevel || selectedVipLevel === formData.vipLevel) {
        setVipUpgradeInfo(null);
        return;
      }

      try {
        const currentVip = formData.vipLevel || 'VIP1';
        const targetVip = selectedVipLevel;
        
        // Get current VIP level number
        const getVipNumber = (vip) => parseInt(vip.replace('VIP', ''));
        const currentNum = getVipNumber(currentVip);
        const targetNum = getVipNumber(targetVip);

        // Can only upgrade to higher levels
        if (targetNum <= currentNum) {
          setVipUpgradeInfo({
            canUpgrade: false,
            reason: 'Cannot downgrade or set to same level'
          });
          return;
        }

        // Cannot skip levels - must upgrade one at a time
        if (targetNum !== currentNum + 1) {
          setVipUpgradeInfo({
            canUpgrade: false,
            reason: 'Cannot skip levels. Must upgrade one level at a time.'
          });
          return;
        }

        // Get upgrade price for this specific level upgrade
        const upgradeInfo = VIP_UPGRADE_REQUIREMENTS[currentVip];
        if (!upgradeInfo || !upgradeInfo.nextLevel || upgradeInfo.nextLevel !== targetVip) {
          setVipUpgradeInfo({
            canUpgrade: false,
            reason: `Invalid upgrade path: ${currentVip} → ${targetVip}`
          });
          return;
        }

        const upgradePrice = upgradeInfo.upgradePrice;
        const walletBalance = formData.wallet || 0;
        const commissionBalance = formData.commission || 0;
        
        // NEW SYSTEM: Upgrade price is ADDED to balance (not deducted)
        const newWalletBalance = walletBalance + upgradePrice;
        const newTotalBalance = newWalletBalance + commissionBalance;

        setVipUpgradeInfo({
          canUpgrade: true,  // Admin can always upgrade (no balance requirement)
          currentVIP: currentVip,
          targetVIP: targetVip,
          upgradePrice: upgradePrice,
          currentBalance: walletBalance,
          newBalance: newWalletBalance,
          newTotalBalance: newTotalBalance,
          reason: null
        });
      } catch (error) {
        console.error('Error checking upgrade eligibility:', error);
        setVipUpgradeInfo({
          canUpgrade: false,
          reason: 'Error checking eligibility: ' + error.message
        });
      }
    };

    checkUpgradeEligibility();
  }, [user, selectedVipLevel, formData.vipLevel, formData.wallet, formData.commission]);

  // Load optimization status
  const loadOptimizationStatus = async () => {
    try {
      if (!userId) return;
      
      const vipLevel = formData.vipLevel || user?.vipLevel || 'VIP1';
      
      // Get optimization data which includes all progress info
      const optimizationData = await getUserOptimizationData(userId, vipLevel);
      
      if (optimizationData) {
        // Also get completedSets from optimizationStatus
        const statusRef = doc(db, 'optimizationStatus', userId);
        const statusDoc = await getDoc(statusRef);
        const statusData = statusDoc.exists() ? statusDoc.data() : {};
        
        setOptimizationStatus({
          completedSets: statusData.completedSets || 0,
          currentTask: optimizationData.currentTask || 0,
          tasksCompleted: optimizationData.tasksCompleted || 0,
          currentOptimization: optimizationData.currentOptimization || 1,
          isLocked: statusData.isLocked || false,
          vipLevel: vipLevel,
          optimizations: optimizationData.optimizations || {}
        });
      } else {
        setOptimizationStatus({
          completedSets: 0,
          currentTask: 0,
          tasksCompleted: 0,
          currentOptimization: 1,
          isLocked: false,
          vipLevel: vipLevel,
          optimizations: {}
        });
      }
    } catch (error) {
      console.error('Error loading optimization status:', error);
      setOptimizationStatus(null);
    }
  };

  // Handle admin reset progress
  const handleResetProgress = async () => {
    if (!user || !window.confirm(`Are you sure you want to reset the progress for ${user.email || user.username || userId}? This will reset all optimization progress to Set 1, Task 1.`)) {
      return;
    }

    try {
      setResettingProgress(true);
      setError('');
      
      // Verify admin access
      const adminInfo = await verifyAdmin();
      if (!adminInfo.isAdmin) {
        throw new Error('Unauthorized: Admin access required');
      }

      const vipLevel = user.vipLevel || 'VIP1';
      await resetUserProgress(userId, vipLevel);
      
      setSuccess('User progress has been reset successfully!');
      
      // Reload optimization status
      await loadOptimizationStatus();
      
      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Error resetting progress:', err);
      setError(err.message || 'Failed to reset user progress');
    } finally {
      setResettingProgress(false);
    }
  };

  const loadUser = async () => {
    try {
      setLoading(true);
      setError('');
      
      // Verify admin access
      const adminStatus = await verifyAdmin();
      if (!adminStatus.isAdmin) {
        throw new Error('Unauthorized: Admin access required');
      }
      
      const userData = await fetchUserById(userId);
      if (userData) {
        setUser(userData);
        // Calculate total: Total = Wallet + Commission (excluding frozen)
        const calculatedTotal = (userData.balances?.wallet || 0) + (userData.balances?.commission || 0);
        setFormData({
          email: userData.email || '',
          username: userData.username || '',
          vipLevel: userData.vipLevel || 'VIP1',
          creditScore: userData.creditScore ?? 100,
          wallet: userData.balances?.wallet || 0,
          frozen: userData.balances?.frozen || 0,
          commission: userData.balances?.commission || 0,
          total: calculatedTotal,
          isActive: userData.isActive !== false
        });
      }
    } catch (error) {
      console.error('Error loading user:', error);
      setError(error.message || 'Failed to load user data');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    // Special handling for VIP level - don't update formData directly
    if (name === 'vipLevel') {
      setSelectedVipLevel(value);
      setError('');
      setSuccess('');
      return;
    }
    
    setFormData(prev => {
      const newData = {
        ...prev,
        [name]: type === 'checkbox' ? checked : (type === 'number' ? parseFloat(value) || 0 : value)
      };
      
      return newData;
    });
  };

  // Handle VIP level upgrade (only admin can do this)
  // NEW SYSTEM: Upgrade price is ADDED to user's balance
  const handleVIPUpgrade = async () => {
    if (!selectedVipLevel || !vipUpgradeInfo || !vipUpgradeInfo.canUpgrade) {
      return;
    }

    if (!window.confirm(
      `Upgrade user from ${vipUpgradeInfo.currentVIP} to ${vipUpgradeInfo.targetVIP}?\n\n` +
      `💰 ${vipUpgradeInfo.upgradePrice} USDT will be CREDITED to their wallet balance.\n\n` +
      `Current Balance: ${vipUpgradeInfo.currentBalance.toFixed(2)} USDT\n` +
      `New Balance: ${vipUpgradeInfo.newBalance.toFixed(2)} USDT\n\n` +
      `This action cannot be undone.`
    )) {
      return;
    }

    setUpgrading(true);
    setError('');
    setSuccess('');

    try {
      // Verify admin access
      const adminInfo = await verifyAdmin();
      if (!adminInfo.isAdmin) {
        throw new Error('Unauthorized: Admin access required');
      }

      // Get current user info for admin tracking
      const currentUser = auth.currentUser;
      if (!currentUser) {
        throw new Error('Not authenticated');
      }

      // Process upgrade (one level at a time - validation ensures this)
      const result = await processVIPUpgrade(
        userId, 
        vipUpgradeInfo.targetVIP,  // Target VIP level
        currentUser.uid,  // Admin ID
        currentUser.email  // Admin Email
      );
      
      if (!result.success) {
        throw new Error(`Failed to upgrade from ${vipUpgradeInfo.currentVIP} to ${vipUpgradeInfo.targetVIP}`);
      }

      setSuccess(
        `✅ User successfully upgraded to ${vipUpgradeInfo.targetVIP}!\n` +
        `${vipUpgradeInfo.upgradePrice} USDT has been credited to their wallet balance.`
      );
      setSelectedVipLevel(null);
      setVipUpgradeInfo(null);
      
      // Reload user data to reflect changes
      await loadUser();
    } catch (err) {
      console.error('Error upgrading VIP:', err);
      setError(err.message || 'Failed to upgrade VIP level');
    } finally {
      setUpgrading(false);
    }
  };

  // Auto-calculate total balance: Total = Wallet + Commission (commission is included in wallet display)
  const calculatedTotal = useMemo(() => {
    return (formData.wallet || 0) + (formData.commission || 0);
  }, [formData.wallet, formData.commission]);

  // Update total when wallet or commission changes
  useEffect(() => {
    setFormData(prev => ({
      ...prev,
      total: calculatedTotal
    }));
  }, [calculatedTotal]);

  const handleCreditScoreChange = (e) => {
    const value = Math.min(100, Math.max(0, parseInt(e.target.value) || 0));
    setFormData(prev => ({ ...prev, creditScore: value }));
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(() => {
      setSuccess('Copied to clipboard!');
      setTimeout(() => setSuccess(''), 2000);
    });
  };

  const getVipBadgeColor = (level) => {
    const colors = {
      'VIP1': '#6c757d',
      'VIP2': '#0d6efd',
      'VIP3': '#ffc107',
      'VIP4': '#dc3545'
    };
    return colors[level] || colors['VIP1'];
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    if (timestamp.toDate) {
      return timestamp.toDate().toLocaleDateString('en-US', { 
        year: 'numeric', 
        month: 'long', 
        day: 'numeric' 
      });
    }
    return new Date(timestamp).toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    
    // Verify admin access
    if (!isAdmin) {
      setError('Unauthorized: Admin access required');
      return;
    }

    setSaving(true);

    try {
      // Validate sensitive field changes
      const sensitiveFields = ['role', 'admin', 'isDeleted'];
      const hasSensitiveChanges = Object.keys(formData).some(key => sensitiveFields.includes(key));
      
      if (hasSensitiveChanges && (!reason || reason.trim() === '')) {
        throw new Error('Reason is required when modifying sensitive fields');
      }

      // Calculate total balance: Total = Wallet + Commission (excluding frozen)
      // Note: Balances are read-only, but we still calculate total for consistency
      const calculatedTotal = (formData.wallet || 0) + (formData.commission || 0);

      // IMPORTANT: VIP level is NOT included in regular updates
      // VIP level can ONLY be changed through the upgrade process which credits balance
      const updates = {
        email: formData.email,
        username: formData.username,
        // vipLevel is intentionally excluded - must use upgrade process
        creditScore: formData.creditScore,
        balances: {
          wallet: formData.wallet,
          frozen: formData.frozen,
          commission: formData.commission,
          total: calculatedTotal
        },
        isActive: formData.isActive
      };

      await updateUser(userId, updates, reason || 'No reason provided');
      setSuccess('User updated successfully!');
      
      if (onUpdate) {
        onUpdate();
      }
      
      setTimeout(() => {
        onBack();
      }, 1500);
    } catch (error) {
      console.error('Error updating user:', error);
      setError(error.message || 'Failed to update user');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="admin-edit-loading">
        <div className="admin-edit-spinner">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-3 text-muted">Loading user data...</p>
        </div>
      </div>
    );
  }

  // Show error if unauthorized
  if (!isAdmin) {
    return (
      <div className="admin-edit-error">
        <div className="alert alert-danger">
          <i className="bi bi-exclamation-triangle me-2"></i>
          Unauthorized: Admin access required
          <button className="btn btn-sm btn-secondary ms-2" onClick={onBack}>Go Back</button>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="admin-edit-error">
        <div className="alert alert-danger">
          <i className="bi bi-exclamation-triangle me-2"></i>
          User not found
          <button className="btn btn-sm btn-secondary ms-2" onClick={onBack}>Go Back</button>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-edit-form-container">
      {/* Header */}
      <div className="admin-edit-header">
        <div className="admin-edit-header-content">
          <div>
            <h2 className="admin-edit-title">
              <i className="bi bi-person-gear me-2"></i>
              Edit User Profile
            </h2>
            <p className="admin-edit-subtitle">Manage user account settings and information</p>
          </div>
          <button className="admin-edit-back-btn" onClick={onBack}>
            <i className="bi bi-arrow-left me-2"></i>
            Back to Users
          </button>
        </div>
      </div>

      {/* Messages */}
      {error && (
        <div className="admin-edit-message admin-edit-error-message">
          <i className="bi bi-exclamation-circle me-2"></i>
          {error}
        </div>
      )}

      {success && (
        <div className="admin-edit-message admin-edit-success-message">
          <i className="bi bi-check-circle me-2"></i>
          {success}
        </div>
      )}

      <form onSubmit={handleSubmit} className="admin-edit-form">
        {/* Section 1: Personal Information */}
        <div className="admin-edit-section">
          <div className="admin-edit-section-header">
            <div className="admin-edit-section-icon">
              <i className="bi bi-person-circle"></i>
            </div>
            <div>
              <h3 className="admin-edit-section-title">Personal Information</h3>
              <p className="admin-edit-section-subtitle">User account details and identification</p>
            </div>
          </div>
          <div className="admin-edit-section-body">
            <div className="admin-edit-grid">
              <div className="admin-edit-field">
                <label htmlFor="email" className="admin-edit-label">
                  <i className="bi bi-envelope me-2"></i>
                  Email Address
                </label>
                <div className="admin-edit-input-wrapper">
                  <input
                    type="email"
                    className="admin-edit-input"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    placeholder="user@example.com"
                  />
                  <button
                    type="button"
                    className="admin-edit-copy-btn"
                    onClick={() => copyToClipboard(formData.email)}
                    title="Copy to clipboard"
                  >
                    <i className="bi bi-clipboard"></i>
                  </button>
                </div>
              </div>

              <div className="admin-edit-field">
                <label htmlFor="username" className="admin-edit-label">
                  <i className="bi bi-person me-2"></i>
                  Username
                </label>
                <input
                  type="text"
                  className="admin-edit-input"
                  id="username"
                  name="username"
                  value={formData.username}
                  onChange={handleChange}
                  placeholder="Enter username"
                />
              </div>

              <div className="admin-edit-field">
                <label className="admin-edit-label">
                  <i className="bi bi-calendar me-2"></i>
                  Registration Date
                </label>
                <div className="admin-edit-readonly">
                  {formatDate(user.createdAt || user.timestamp)}
                </div>
              </div>

              <div className="admin-edit-field">
                <label className="admin-edit-label">
                  <i className="bi bi-fingerprint me-2"></i>
                  User ID
                </label>
                <div className="admin-edit-input-wrapper">
                  <div className="admin-edit-readonly admin-edit-readonly-id">{userId}</div>
                  <button
                    type="button"
                    className="admin-edit-copy-btn"
                    onClick={() => copyToClipboard(userId)}
                    title="Copy to clipboard"
                  >
                    <i className="bi bi-clipboard"></i>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Section 2: VIP & Status */}
        <div className="admin-edit-section">
          <div className="admin-edit-section-header">
            <div className="admin-edit-section-icon">
              <i className="bi bi-award"></i>
            </div>
            <div>
              <h3 className="admin-edit-section-title">VIP & Status</h3>
              <p className="admin-edit-section-subtitle">User tier level and account status</p>
            </div>
          </div>
          <div className="admin-edit-section-body">
            <div className="admin-edit-grid">
              <div className="admin-edit-field">
                <label htmlFor="vipLevel" className="admin-edit-label">
                  <i className="bi bi-star me-2"></i>
                  VIP Level
                  <span className="admin-edit-readonly-badge ms-2">Admin Only</span>
                </label>
                <div className="admin-edit-select-wrapper">
                  <select
                    className="admin-edit-select"
                    id="vipLevel"
                    name="vipLevel"
                    value={selectedVipLevel || formData.vipLevel}
                    onChange={handleChange}
                    disabled={upgrading || !isAdmin}
                  >
                    <option value="VIP1">VIP1</option>
                    <option value="VIP2">VIP2</option>
                    <option value="VIP3">VIP3</option>
                    <option value="VIP4">VIP4</option>
                  </select>
                  <span
                    className="admin-edit-vip-badge"
                    style={{ backgroundColor: getVipBadgeColor(selectedVipLevel || formData.vipLevel) }}
                  >
                    {selectedVipLevel || formData.vipLevel}
                  </span>
                </div>
                <small className="admin-edit-hint">
                  <i className="bi bi-info-circle me-1"></i>
                  Select a VIP level to upgrade. Upgrade price will be automatically credited to user's wallet balance.
                </small>
                
                {/* VIP Upgrade Info Section */}
                {selectedVipLevel && selectedVipLevel !== formData.vipLevel && vipUpgradeInfo && (
                  <div className="mt-3 p-3" style={{ 
                    backgroundColor: vipUpgradeInfo.canUpgrade ? 'rgba(16, 185, 129, 0.1)' : 'rgba(239, 68, 68, 0.1)', 
                    borderRadius: '8px',
                    border: `2px solid ${vipUpgradeInfo.canUpgrade ? 'rgba(16, 185, 129, 0.3)' : 'rgba(239, 68, 68, 0.3)'}`
                  }}>
                    {vipUpgradeInfo.canUpgrade ? (
                      <>
                        <div className="d-flex justify-content-between align-items-center mb-2">
                          <div>
                            <strong className="text-success">
                              <i className="bi bi-check-circle me-2"></i>
                              Upgrade Available
                            </strong>
                            <div className="small text-muted mt-1">
                              {vipUpgradeInfo.currentVIP} → {vipUpgradeInfo.targetVIP}
                            </div>
                          </div>
                          <button
                            className="btn btn-sm btn-success"
                            onClick={handleVIPUpgrade}
                            disabled={upgrading}
                          >
                            {upgrading ? (
                              <>
                                <span className="spinner-border spinner-border-sm me-2"></span>
                                Upgrading...
                              </>
                            ) : (
                              <>
                                <i className="bi bi-arrow-up me-2"></i>
                                Upgrade Now
                              </>
                            )}
                          </button>
                        </div>
                        <div className="small text-muted">
                          <div><strong>Upgrade Price (Credited):</strong> <span className="text-success">+{vipUpgradeInfo.upgradePrice} USDT</span></div>
                          <div><strong>Current Balance:</strong> {vipUpgradeInfo.currentBalance.toFixed(2)} USDT</div>
                          <div><strong>New Balance After Upgrade:</strong> <span className="text-success">{vipUpgradeInfo.newBalance.toFixed(2)} USDT</span> ✅</div>
                        </div>
                      </>
                    ) : (
                      <div>
                        <strong className="text-danger">
                          <i className="bi bi-exclamation-triangle me-2"></i>
                          {vipUpgradeInfo.reason || 'Cannot Upgrade'}
                        </strong>
                        <div className="small text-muted mt-2">
                          <div><strong>Upgrade Price:</strong> {vipUpgradeInfo.upgradePrice || 'N/A'} USDT</div>
                          <div><strong>Current Balance:</strong> {vipUpgradeInfo.currentBalance?.toFixed(2) || '0.00'} USDT</div>
                          {vipUpgradeInfo.reason && (
                            <div className="text-danger mt-1">
                              <i className="bi bi-x-circle me-1"></i>
                              {vipUpgradeInfo.reason}
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                )}
                
                {/* Show current level info when no upgrade selected */}
                {(!selectedVipLevel || selectedVipLevel === formData.vipLevel) && (
                  <div className="mt-2 small text-muted">
                    <i className="bi bi-info-circle me-1"></i>
                    Current Level: <strong>{formData.vipLevel}</strong> | Wallet Balance: <strong>{formData.wallet.toFixed(2)} USDT</strong>
                  </div>
                )}
              </div>

              <div className="admin-edit-field">
                <label htmlFor="creditScore" className="admin-edit-label">
                  <i className="bi bi-graph-up me-2"></i>
                  Credit Score
                  <span className="admin-edit-value-display">({formData.creditScore}/100)</span>
                </label>
                <div className="admin-edit-range-wrapper">
                  <input
                    type="range"
                    className="admin-edit-range"
                    id="creditScore"
                    name="creditScore"
                    value={formData.creditScore}
                    onChange={handleCreditScoreChange}
                    min="0"
                    max="100"
                  />
                  <input
                    type="number"
                    className="admin-edit-input admin-edit-number-input"
                    value={formData.creditScore}
                    onChange={handleCreditScoreChange}
                    min="0"
                    max="100"
                  />
                </div>
                <div className="admin-edit-progress-bar">
                  <div 
                    className="admin-edit-progress-fill"
                    style={{ width: `${formData.creditScore}%` }}
                  ></div>
                </div>
              </div>

              <div className="admin-edit-field admin-edit-field-full">
                <label className="admin-edit-label">
                  <i className="bi bi-toggle-on me-2"></i>
                  Account Status
                </label>
                <div className="admin-edit-toggle-wrapper">
                  <label className="admin-edit-toggle">
                    <input
                      type="checkbox"
                      name="isActive"
                      checked={formData.isActive}
                      onChange={handleChange}
                    />
                    <span className="admin-edit-toggle-slider"></span>
                  </label>
                  <span className={`admin-edit-status-badge ${formData.isActive ? 'active' : 'inactive'}`}>
                    <i className={`bi bi-${formData.isActive ? 'check-circle' : 'x-circle'} me-1`}></i>
                    {formData.isActive ? 'Active' : 'Suspended'}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Section 3: Financial Balances */}
        <div className="admin-edit-section">
          <div className="admin-edit-section-header">
            <div className="admin-edit-section-icon">
              <i className="bi bi-wallet2"></i>
            </div>
            <div>
              <h3 className="admin-edit-section-title">Financial Balances</h3>
              <p className="admin-edit-section-subtitle">User account balances in USDT</p>
            </div>
          </div>
          <div className="admin-edit-section-body">
            <div className="admin-edit-grid admin-edit-grid-4">
              <div className="admin-edit-field">
                <label htmlFor="wallet" className="admin-edit-label">
                  <i className="bi bi-wallet me-2"></i>
                  Wallet Balance
                  <span className="admin-edit-readonly-badge">Read-only</span>
                </label>
                <div className="admin-edit-currency-input">
                  <input
                    type="number"
                    step="0.01"
                    className="admin-edit-input admin-edit-readonly-input"
                    id="wallet"
                    name="wallet"
                    value={formData.wallet}
                    readOnly
                    min="0"
                    placeholder="0.00"
                  />
                  <span className="admin-edit-currency">USDT</span>
                </div>
                <small className="admin-edit-hint">
                  <i className="bi bi-info-circle me-1"></i>
                  Use Deposit Management to modify wallet balance
                </small>
              </div>

              <div className="admin-edit-field">
                <label htmlFor="frozen" className="admin-edit-label">
                  <i className="bi bi-lock me-2"></i>
                  Frozen Balance
                  <span className="admin-edit-readonly-badge">Read-only</span>
                </label>
                <div className="admin-edit-currency-input">
                  <input
                    type="number"
                    step="0.01"
                    className="admin-edit-input admin-edit-readonly-input"
                    id="frozen"
                    name="frozen"
                    value={formData.frozen}
                    readOnly
                    min="0"
                    placeholder="0.00"
                  />
                  <span className="admin-edit-currency">USDT</span>
                </div>
                <small className="admin-edit-hint">
                  <i className="bi bi-info-circle me-1"></i>
                  Frozen balance is managed by the system
                </small>
              </div>

              <div className="admin-edit-field">
                <label htmlFor="commission" className="admin-edit-label">
                  <i className="bi bi-percent me-2"></i>
                  Commission
                  <span className="admin-edit-readonly-badge">Read-only</span>
                </label>
                <div className="admin-edit-currency-input">
                  <input
                    type="number"
                    step="0.01"
                    className="admin-edit-input admin-edit-readonly-input"
                    id="commission"
                    name="commission"
                    value={formData.commission}
                    readOnly
                    min="0"
                    placeholder="0.00"
                  />
                  <span className="admin-edit-currency">USDT</span>
                </div>
                <small className="admin-edit-hint">
                  <i className="bi bi-info-circle me-1"></i>
                  Commission is calculated automatically
                </small>
              </div>

              <div className="admin-edit-field">
                <label htmlFor="total" className="admin-edit-label">
                  <i className="bi bi-calculator me-2"></i>
                  Total Balance
                  <span className="admin-edit-readonly-badge">Auto-calculated</span>
                </label>
                <div className="admin-edit-currency-input admin-edit-total-balance">
                  <input
                    type="number"
                    step="0.01"
                    className="admin-edit-input admin-edit-readonly-input"
                    id="total"
                    name="total"
                    value={calculatedTotal.toFixed(2)}
                    readOnly
                    placeholder="0.00"
                  />
                  <span className="admin-edit-currency">USDT</span>
                </div>
                <small className="admin-edit-hint">
                  <i className="bi bi-info-circle me-1"></i>
                  Total = Wallet + Commission (Frozen balance excluded)
                </small>
              </div>
            </div>
          </div>
        </div>

        {/* Section 4: Optimization Progress Status */}
        <div className="admin-edit-section">
          <div className="admin-edit-section-header">
            <div className="admin-edit-section-icon">
              <i className="bi bi-graph-up"></i>
            </div>
            <div>
              <h3 className="admin-edit-section-title">Optimization Progress</h3>
              <p className="admin-edit-section-subtitle">Daily progress status and completion</p>
            </div>
          </div>
          <div className="admin-edit-section-body">
            {optimizationStatus ? (() => {
              const vipLevel = formData.vipLevel || 'VIP1';
              const config = VIP_CONFIG[vipLevel] || VIP_CONFIG['VIP1'];
              const totalSets = config.optimizations || config.sets || 3;
              const completedSets = optimizationStatus.completedSets || 0;
              const isComplete = completedSets >= totalSets;
              const currentTask = optimizationStatus.currentTask || 0;
              const tasksCompleted = optimizationStatus.tasksCompleted || 0;
              
              return (
                <div className="admin-edit-field admin-edit-field-full">
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <div>
                      <label className="admin-edit-label mb-2">
                        <i className="bi bi-check-circle me-2"></i>
                        Daily Progress Status
                      </label>
                      <div className="d-flex align-items-center gap-3">
                        <div>
                          <span className="text-muted">Completed Sets: </span>
                          <strong className={isComplete ? 'text-success' : 'text-primary'}>
                            {completedSets} / {totalSets}
                          </strong>
                        </div>
                        <div>
                          <span className="text-muted">Current Task: </span>
                          <strong>{currentTask}</strong>
                        </div>
                        <div>
                          <span className="text-muted">Tasks Completed: </span>
                          <strong>{tasksCompleted}</strong>
                        </div>
                      </div>
                    </div>
                    {isComplete && (
                      <div className="badge bg-success fs-6 px-3 py-2">
                        <i className="bi bi-check-circle-fill me-2"></i>
                        Daily Progress Complete
                      </div>
                    )}
                  </div>
                  
                  {/* Progress Bar */}
                  <div className="mb-3">
                    <div className="progress" style={{ height: '25px' }}>
                      <div 
                        className={`progress-bar ${isComplete ? 'bg-success' : 'bg-primary'}`}
                        role="progressbar"
                        style={{ width: `${Math.min((completedSets / totalSets) * 100, 100)}%` }}
                        aria-valuenow={completedSets}
                        aria-valuemin="0"
                        aria-valuemax={totalSets}
                      >
                        {Math.round((completedSets / totalSets) * 100)}%
                      </div>
                    </div>
                  </div>
                  
                  {/* Action Buttons */}
                  <div className="d-flex justify-content-end gap-2">
                    <button
                      type="button"
                      className="btn btn-outline-primary"
                      onClick={loadOptimizationStatus}
                      disabled={resettingProgress}
                      title="Refresh progress status"
                    >
                      <i className="bi bi-arrow-clockwise me-2"></i>
                      Refresh
                    </button>
                    <button
                      type="button"
                      className="btn btn-warning"
                      onClick={handleResetProgress}
                      disabled={resettingProgress}
                    >
                      {resettingProgress ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                          Resetting...
                        </>
                      ) : (
                        <>
                          <i className="bi bi-arrow-counterclockwise me-2"></i>
                          Reset Progress
                        </>
                      )}
                    </button>
                  </div>
                  <small className="admin-edit-hint">
                    <i className="bi bi-info-circle me-1"></i>
                    Resetting progress will reset all optimization data to Set 1, Task 1. This action cannot be undone.
                  </small>
                </div>
              );
            })() : (
              <div className="admin-edit-field admin-edit-field-full">
                <div className="text-muted text-center py-3">
                  <i className="bi bi-hourglass-split me-2"></i>
                  Loading optimization status...
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Section 5: Admin Actions */}
        <div className="admin-edit-section">
          <div className="admin-edit-section-header">
            <div className="admin-edit-section-icon">
              <i className="bi bi-shield-check"></i>
            </div>
            <div>
              <h3 className="admin-edit-section-title">Admin Actions</h3>
              <p className="admin-edit-section-subtitle">Document changes and confirm updates</p>
            </div>
          </div>
          <div className="admin-edit-section-body">
            <div className="admin-edit-field admin-edit-field-full">
              <label htmlFor="reason" className="admin-edit-label">
                <i className="bi bi-file-text me-2"></i>
                Reason for Changes
                <span className="admin-edit-optional">(Optional - Recommended for audit trail)</span>
              </label>
              <textarea
                className="admin-edit-textarea"
                id="reason"
                rows="4"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="Enter the reason for making these changes. This will be logged in the audit trail..."
              />
              <small className="admin-edit-hint">
                <i className="bi bi-info-circle me-1"></i>
                All changes are automatically logged with timestamp and admin information
              </small>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="admin-edit-actions">
          <button 
            type="submit" 
            className="admin-edit-btn admin-edit-btn-primary" 
            disabled={saving}
          >
            {saving ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                Saving Changes...
              </>
            ) : (
              <>
                <i className="bi bi-save me-2"></i>
                Save Changes
              </>
            )}
          </button>
          <button 
            type="button" 
            className="admin-edit-btn admin-edit-btn-secondary" 
            onClick={onBack} 
            disabled={saving}
          >
            <i className="bi bi-x-circle me-2"></i>
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
}

export default UserEditForm;
